import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Loader2, Shield, Lock, CreditCard, Check, Heart, Zap, Brain, Users } from 'lucide-react';
import PublicHeader from '../components/PublicHeader';
import { CurrencySelector, getStoredCurrency, formatPrice, getCurrencyInfo, setStoredCurrency } from '../components/CurrencySelector';

const API = process.env.REACT_APP_BACKEND_URL;

// Initialize Stripe - MUST use environment variable, no fallback in production
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

// Base prices in AUD
const BASE_PRICES_AUD = {
    essentials_monthly: { amount: 14.99, name: 'Essentials', icon: Zap, color: '#1D6B6B', period: 'mo' },
    essentials_yearly: { amount: 119, name: 'Essentials', icon: Zap, color: '#1D6B6B', period: 'year', savings: 'Save 34%' },
    support_plus_monthly: { amount: 29.99, name: 'Support+', icon: Brain, color: '#1D6B6B', period: 'mo' },
    support_plus_yearly: { amount: 229, name: 'Support+', icon: Brain, color: '#1D6B6B', period: 'year', savings: 'Save 36%' },
    partner_monthly: { amount: 49.99, name: 'Partner', icon: Users, color: '#E08850', period: 'mo' },
    partner_yearly: { amount: 379, name: 'Partner', icon: Users, color: '#E08850', period: 'year', savings: 'Save 37%' },
};

const CheckoutPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { token, isAuthenticated } = useAuth();
    const [clientSecret, setClientSecret] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const packageId = searchParams.get('package');
    const urlCurrency = searchParams.get('currency');
    
    // Initialize currency from URL param or localStorage
    const [currency, setCurrency] = useState(() => {
        if (urlCurrency && ['aud', 'usd', 'gbp', 'eur'].includes(urlCurrency.toLowerCase())) {
            setStoredCurrency(urlCurrency.toLowerCase());
            return urlCurrency.toLowerCase();
        }
        return getStoredCurrency();
    });
    
    // Get package info with dynamic currency
    const getPackageInfo = (pkgId, currencyCode) => {
        const base = BASE_PRICES_AUD[pkgId] || BASE_PRICES_AUD.support_plus_monthly;
        return {
            ...base,
            price: `${formatPrice(base.amount, currencyCode)}/${base.period}`,
        };
    };
    
    const currentPackage = getPackageInfo(packageId, currency);
    const PackageIcon = currentPackage.icon;

    const fetchClientSecret = useCallback(async () => {
        if (!packageId || !token) return;
        
        try {
            const response = await axios.post(
                `${API}/api/stripe/create-embedded-checkout`,
                {
                    package_id: packageId,
                    origin_url: window.location.origin,
                    currency: currency
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            
            setClientSecret(response.data.client_secret);
            setLoading(false);
        } catch (err) {
            console.error('Checkout error:', err);
            setError(err.response?.data?.detail || 'Failed to initialize checkout');
            setLoading(false);
        }
    }, [packageId, token, currency]);

    useEffect(() => {
        // Check if Stripe is properly configured
        if (!process.env.REACT_APP_STRIPE_PUBLIC_KEY) {
            setError('Payment system is not configured. Please contact support.');
            setLoading(false);
            return;
        }
        
        if (!packageId) {
            navigate('/pricing');
            return;
        }
        
        if (!isAuthenticated) {
            navigate('/auth', { state: { returnTo: `/checkout?package=${packageId}&currency=${currency}` } });
            return;
        }
        
        fetchClientSecret();
    }, [packageId, isAuthenticated, navigate, fetchClientSecret, currency]);

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FDF8F4] flex flex-col">
                <PublicHeader 
                    showBackButton={true}
                    backButtonText="Back to Pricing"
                    backButtonHref="/pricing"
                />
                <div className="h-16"></div>
                <main className="flex-1 flex items-center justify-center px-4">
                    <div className="text-center max-w-md">
                        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
                            <CreditCard className="w-8 h-8 text-red-500" />
                        </div>
                        <h1 className="text-2xl font-bold text-[#1D3D3D] mb-3">Unable to Load Checkout</h1>
                        <p className="text-[#5A7A7A] mb-6">{error}</p>
                        <button
                            onClick={() => navigate('/pricing')}
                            className="px-6 py-3 bg-[#1D6B6B] text-white rounded-xl font-medium hover:bg-[#145555] transition-colors"
                        >
                            Back to Pricing
                        </button>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FDF8F4] flex flex-col">
            <PublicHeader 
                showBackButton={true}
                backButtonText="Back to Pricing"
                backButtonHref="/pricing"
            />
            
            {/* Spacer for fixed header */}
            <div className="h-16"></div>
            
            <main className="flex-1 max-w-6xl mx-auto w-full px-4 py-8">
                <div className="grid lg:grid-cols-5 gap-8">
                    {/* Left Side - Order Summary */}
                    <div className="lg:col-span-2 order-2 lg:order-1">
                        <div className="bg-white rounded-2xl shadow-lg border border-[#E8E1D6] p-6 sticky top-24">
                            <h2 className="text-lg font-semibold text-[#1D3D3D] mb-6">Order Summary</h2>
                            
                            {/* Package Card */}
                            <div className="bg-white/70 rounded-xl p-4 mb-6">
                                <div className="flex items-start gap-4">
                                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center`} style={{ backgroundColor: `${currentPackage.color}15` }}>
                                        <PackageIcon className="w-6 h-6" style={{ color: currentPackage.color }} />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-semibold text-[#1D3D3D]">{currentPackage.name} Plan</h3>
                                        <p className="text-sm text-[#7D8E95]">CoRegulate Subscription</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-bold text-[#1D3D3D]">{currentPackage.price}</p>
                                        {currentPackage.savings && (
                                            <span className="text-xs px-2 py-0.5 rounded bg-[#E08850]/10 text-[#E08850] font-medium">
                                                {currentPackage.savings}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {/* What's Included */}
                            <div className="mb-6">
                                <h4 className="text-sm font-medium text-[#7D8E95] mb-3">What's included:</h4>
                                <ul className="space-y-2">
                                    {getFeatures(packageId).map((feature, idx) => (
                                        <li key={idx} className="flex items-center gap-2 text-sm text-[#5A7A7A]">
                                            <Check className="w-4 h-4 text-[#1D6B6B]" />
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            
                            {/* Trust Badges */}
                            <div className="border-t border-[#E8E1D6] pt-6 space-y-3">
                                <div className="flex items-center gap-3 text-sm text-[#7D8E95]">
                                    <Shield className="w-5 h-5 text-[#1D6B6B]" />
                                    <span>Secure 256-bit SSL encryption</span>
                                </div>
                                <div className="flex items-center gap-3 text-sm text-[#7D8E95]">
                                    <Lock className="w-5 h-5 text-[#1D6B6B]" />
                                    <span>Cancel anytime, no questions asked</span>
                                </div>
                                <div className="flex items-center gap-3 text-sm text-[#7D8E95]">
                                    <Heart className="w-5 h-5 text-[#1D6B6B]" />
                                    <span>Made for ADHD minds</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Right Side - Payment Form */}
                    <div className="lg:col-span-3 order-1 lg:order-2">
                        <div className="bg-white rounded-2xl shadow-lg border border-[#E8E1D6] overflow-hidden">
                            {/* Payment Header */}
                            <div className="bg-white/70 px-6 py-5 border-b border-[#E8E1D6]">
                                <h2 className="text-xl font-semibold text-[#1D3D3D]">Complete Your Purchase</h2>
                                <p className="text-[#7D8E95] text-sm mt-1">Enter your payment details below</p>
                            </div>
                            
                            {/* Stripe Embedded Checkout */}
                            <div className="p-6">
                                {loading ? (
                                    <div className="flex flex-col items-center justify-center py-16">
                                        <Loader2 className="w-10 h-10 animate-spin text-[#1D6B6B] mb-4" />
                                        <p className="text-[#7D8E95]">Loading secure checkout...</p>
                                    </div>
                                ) : clientSecret ? (
                                    <EmbeddedCheckoutProvider
                                        stripe={stripePromise}
                                        options={{ clientSecret }}
                                    >
                                        <EmbeddedCheckout />
                                    </EmbeddedCheckoutProvider>
                                ) : null}
                            </div>
                        </div>
                        
                        {/* Powered by Stripe */}
                        <div className="flex items-center justify-center gap-2 mt-6 text-sm text-[#7D8E95]">
                            <Lock className="w-4 h-4" />
                            <span>Payments secured by</span>
                            <svg className="h-5" viewBox="0 0 60 25" fill="none">
                                <path d="M59.64 14.28c0-4.88-2.37-8.73-6.9-8.73-4.55 0-7.3 3.85-7.3 8.7 0 5.74 3.25 8.63 7.91 8.63 2.27 0 3.99-.52 5.29-1.24v-3.82c-1.3.67-2.8 1.08-4.69 1.08-1.86 0-3.51-.65-3.72-2.92h9.38c0-.25.03-1.24.03-1.7zm-9.48-1.81c0-2.17 1.33-3.08 2.54-3.08 1.18 0 2.43.91 2.43 3.08h-4.97zM38.3 5.55c-1.88 0-3.08.88-3.75 1.49l-.25-1.19h-4.22v22.12l4.79-1.02.01-5.37c.69.5 1.7 1.21 3.38 1.21 3.42 0 6.53-2.75 6.53-8.81.01-5.54-3.16-8.43-6.49-8.43zm-1.14 12.97c-1.12 0-1.79-.4-2.25-.9l-.02-7.1c.49-.55 1.18-.93 2.27-.93 1.74 0 2.94 1.95 2.94 4.45 0 2.56-1.18 4.48-2.94 4.48zM25.03 4.2l4.8-1.03V0l-4.8 1.02v3.18zM25.03 5.77h4.8v16.66h-4.8V5.77zM20.15 7.04l-.3-1.27h-4.14v16.66h4.79v-11.3c1.13-1.48 3.05-1.21 3.65-1v-4.41c-.62-.23-2.88-.67-4 1.32zM10.74 2.27l-4.67.99-.02 15.26c0 2.82 2.12 4.9 4.94 4.9 1.56 0 2.71-.29 3.34-.63v-3.88c-.61.25-3.61 1.13-3.61-1.7V9.6h3.61V5.77H10.72l.02-3.5zM1.24 10.08c0-.68.56-1.18 1.47-1.18 1.32 0 2.98.4 4.3 1.11V5.89c-1.44-.57-2.87-.8-4.3-.8C1.01 5.09 0 7.6 0 9.88c0 3.64 5.01 3.06 5.01 4.64 0 .81-.7 1.07-1.68 1.07-1.46 0-3.32-.6-4.8-1.41v4.19c1.63.7 3.29 1 4.8 1 2.76 0 5.29-1.14 5.29-4.06.01-3.92-5.38-3.23-5.38-4.73z" fill="#6772e5"/>
                            </svg>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default CheckoutPage;

// Get features based on package
const getFeatures = (packageId) => {
    const base = ['Access to regulation tools', 'Progress tracking', 'Save favourites'];
    
    if (packageId?.includes('essentials')) {
        return [...base, 'All breathing patterns', 'Micro-reset tool', 'Body scan meditation'];
    }
    if (packageId?.includes('support_plus')) {
        return [...base, 'AI Guide companion', 'Guided meditations', 'Self-talk reframes', 'Pause & Repair scripts'];
    }
    if (packageId?.includes('partner')) {
        return [...base, 'AI Guide companion', 'Partner co-regulation', 'Shared sessions', 'Communication scripts'];
    }
    return base;
};
